/**
 * Copyright © 2026 Adnuntius AS.
 */
import {ChangeDetectorRef, Component, Inject, Input, OnChanges, OnDestroy, OnInit, SimpleChanges, signal} from '@angular/core';
import {TranslateService} from '@ngx-translate/core';
import * as _ from 'lodash';
import * as moment from 'moment';
import {AppEventBusService} from '../events/app-event-bus.service';

import {deferMarkForCheck} from '../util/defer-mark-for-check';
import {createBufferedFlag, readBufferedFlag} from '../util/template-state';
import template from './top-stats-table.component.html';

import {OBJECT_TYPE} from '../constants/object-type';
import {StatsResource} from '../../../components/api/resources/resource/stats-resources/stats-resource';
import {Downloader} from '../../../components/util/downloader';
import {OgTranslateService} from '../filters/og-translate.service';
import {AdnLogService} from '../../../components/util/adn-log.service';
import {ChartOptionsUtil} from '../options/chart-options-util.service';

@Component({
  standalone: false,
  selector: 'adn-top-stats-table',
  template
})
export class TopStatsTableComponent implements OnInit, OnChanges, OnDestroy {
  @Input() object: any;
  @Input() parentType: string;
  @Input() childType: string;
  @Input() statsHook: any;
  @Input() currency: any;
  @Input() useAlt: any;
  @Input() blockLink: any;
  @Input() dateSelectorId: string;
  @Input() topN: any;
  @Input() callbackHook: any;

  sel: any = {};
  sorting = {
    orderByParam: 'impressions',
    asc: false
  };

  dataParam: string;
  parentParam: string;
  stateName: string;
  defaultTopN = 50;
  showButton = false;
  loaded = false;
  private readonly loadedFlag = createBufferedFlag();
  private destroyed = false;
  error = false;
  dataTotals: any[] = null;
  otherRow: any = null;
  totals: any = null;
  totalCount: number;
  readonly startDate = signal<any>('UNDEFINED');
  readonly endDate = signal<any>(undefined);
  canceller: any;

  downloadFields: string[];
  downloadColumnOrder: string[];
  csvTableTitle: string;

  private chartDateListener: () => void;
  private allFields: any[];
  private chartOptionObject: any;
  private groupByObject: any;
  private configured = false;

  get resolvedParentType(): string {
    return this.parentType;
  }

  get resolvedChildType(): string {
    return this.childType;
  }

  get resolvedUseAlt(): any {
    return this.useAlt;
  }

  get resolvedDateSelectorId(): string {
    return this.dateSelectorId;
  }

  get displayLoaded(): boolean {
    return readBufferedFlag(this.loadedFlag, this.loaded, this.cdr, () => !this.destroyed);
  }

  constructor(
    private readonly statsResource: StatsResource,
    private readonly eventBus: AppEventBusService,
    private readonly adnLog: AdnLogService,
    private readonly translate: TranslateService,
    private readonly ogTranslate: OgTranslateService,
    @Inject(ChartOptionsUtil) private readonly ChartOptionsUtil: any,
    private readonly cdr: ChangeDetectorRef
  ) {
  }

  ngOnInit(): void {
    this.tryInit();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.parentType || changes.childType) {
      this.tryInit();
    }
  }

  private tryInit(): void {
    this.chartOptionObject = OBJECT_TYPE[this.resolvedParentType];
    this.groupByObject = OBJECT_TYPE[this.resolvedChildType];
    if (!this.chartOptionObject || !this.groupByObject) {
      return;
    }
    if (this.configured) {
      return;
    }
    this.configured = true;
    this.defaultTopN = 50;
    this.topN = this.topN || this.defaultTopN;

    const chartOptions = this.ChartOptionsUtil.getOptions(this.chartOptionObject.caps);
    this.dataParam = this.groupByObject.apiParam;
    this.parentParam = this.chartOptionObject.apiParam;
    this.stateName = this.groupByObject.stateName;

    const fieldKeyPrefix = _.get(this.groupByObject, ['apiParam']);
    const chartOptionsFieldsKey = fieldKeyPrefix ? fieldKeyPrefix + 'Fields' : 'totalsFields';
    this.allFields = chartOptions[chartOptionsFieldsKey];

    this.showButton = typeof this.callbackHook === 'function';

    const eventName = 'chartDates' + chartOptions.idKey + (this.resolvedDateSelectorId || '');
    const applyChartDates = (data: any) => {
      if (this.startDate() !== 'UNDEFINED' && moment(this.startDate()).isSame(data.startDate) && moment(this.endDate()).isSame(data.endDate) && (!this.currency || this.currency === data.currency)) {
        return;
      }
      // Defer date bindings — lastDates / chartDates can land during parent CD
      // ('UNDEFINED' → Date NG0100 on [adnStartDate]).
      deferMarkForCheck(this.cdr, () => {
        if (this.startDate() !== 'UNDEFINED' && moment(this.startDate()).isSame(data.startDate) && moment(this.endDate()).isSame(data.endDate) && (!this.currency || this.currency === data.currency)) {
          return;
        }

        this.startDate.set(data.startDate);
        this.endDate.set(data.endDate);
        this.currency = data.currency || this.currency;

        this.callForStats();
      });
    };

    this.chartDateListener = this.eventBus.on(eventName, (_event: any, data: any) => {
      applyChartDates(data);
    });

    const lastDates = this.ChartOptionsUtil.getLastChartDates(eventName);
    if (lastDates) {
      applyChartDates(lastDates);
    }
  }

  onTopNChange(newN: number): void {
    this.topN = newN;
    this.dataTotals = null;
    this.error = false;
    this.callForStats();
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    if (this.chartDateListener) {
      this.chartDateListener();
    }
    if (this.canceller) {
      this.canceller.resolve('Destroyed component');
    }
  }

  fieldTranslate(key: string): string {
    return this.ogTranslate.transform(key);
  }

  sortUpdate(field: any): void {
    if (field.key === this.sorting.orderByParam) {
      this.sorting.asc = !this.sorting.asc;
    } else {
      this.sorting.orderByParam = field.key;
      this.sorting.asc = false;
    }

    if (_.isFunction(this.statsHook)) {
      this.statsHook(null, this.sorting);
    }
  }

  sortedDataTotals(): any[] {
    if (!this.dataTotals) {
      return [];
    }
    const key = this.sorting.orderByParam;
    const asc = this.sorting.asc;
    return _.orderBy(this.dataTotals, (row) => {
      const val = row[key];
      if (_.get(val, 'currency')) {
        return val.amount || -1;
      }
      if (_.get(val, 'count')) {
        return val.count || -1;
      }
      return val || -1;
    }, [asc ? 'asc' : 'desc']);
  }

  fieldValue(row: any, field: any): any {
    return row[this.resolvedUseAlt ? (field.altId || field.id) : field.key];
  }

  doCallback(): void {
    if (_.isFunction(this.callbackHook)) {
      this.callbackHook();
    }
  }

  getRows(): any[] {
    return this.dataTotals || [];
  }

  saveXls(): void {
    this.updateValues();
    new Downloader().saveXls2(this.csvTableTitle, this.downloadFields, this.downloadColumnOrder, this.dataTotals || [], this.allFields);
  }

  saveDownloadFields(): void {
    this.updateValues();
  }

  private updateValues(): void {
    this.downloadFields = _.map(this.sel.fullFields, (f) => {
      return f.text || f.name;
    });
    this.downloadFields.unshift(this.translate.instant(this.dataParam));
    this.downloadColumnOrder = _.map(this.sel.fullFields, (f) => {
      return f.key || f.id;
    });
    this.downloadColumnOrder.unshift(this.dataParam + 'name');
    this.csvTableTitle = this.translate.instant(this.dataParam) + ' Totals';

    _.forEach(this.dataTotals, (dt) => {
      dt[this.dataParam + 'name'] = _.get(dt, [this.dataParam, 'name']);
    });
  }

  private callForStats(): void {
    // Date-selector / chart callbacks can run in the same turn as CD. Flipping
    // `loaded` (bound as [resolved] on adn-promise-spinner) synchronously trips NG0100.
    deferMarkForCheck(this.cdr, () => {
      this.loaded = false;
      this.dataTotals = null;
      this.error = false;
      this.fetchStats();
    });
  }

  private fetchStats(): void {
    const params: any = {
      id: this.object.id,
      timeout: 180000,
      idKey: this.chartOptionObject.apiParam + 'Id',
      groupBy: this.groupByObject.caps,
      currency: this.currency
    };

    if (this.resolvedParentType === 'UserSegment') {
      params.idKey = 'segmentTargets';
    }

    if (this.startDate() && this.endDate()) {
      params.startDate = this.startDate();
      params.endDate = this.endDate();
    }

    params.topN = this.topN || 50;

    this.statsResource.get(params, (data: any) => {
      const dataTotals = data.chunks || [];
      const allOthersIndex = _.findIndex(dataTotals, (chunk: any) => {
        return chunk[this.dataParam].name === 'All Others' && !chunk[this.dataParam].id;
      });

      const otherRow: any = allOthersIndex > -1 ? _.pullAt(dataTotals, allOthersIndex)[0] : null;
      if (otherRow) {
        const remainingObjects = data.totalCount - params.topN;
        const translationField = this.groupByObject.apiParam + (remainingObjects === 1 ? '' : '.plural');
        otherRow.updatedName = remainingObjects + ' remaining ' + this.translate.instant(translationField);
      }

      deferMarkForCheck(this.cdr, () => {
        this.dataTotals = dataTotals;
        this.otherRow = otherRow;
        this.totals = data.totals;
        this.totalCount = data.totalCount;
        this.loaded = true;
        if (_.isFunction(this.statsHook)) {
          this.statsHook(this.dataTotals, this.sorting);
        }
      });
    }, (err: any) => {
      this.adnLog.warn(err);
      deferMarkForCheck(this.cdr, () => {
        this.error = true;
        this.loaded = true;
      });
    }, this);
  }
}
